import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';

const DEFAULT_CLAMP_LINES = 4;

/** Compact expandable text for work-log / maintenance expanded rows. */
function ClampedDetailsText({ text, lines = DEFAULT_CLAMP_LINES }) {
  const textRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [needsClamp, setNeedsClamp] = useState(false);
  const content = typeof text === 'string' ? text.trim() : (text || '');

  useEffect(() => {
    setExpanded(false);
  }, [content]);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const measure = () => {
      const styles = window.getComputedStyle(el);
      const fontSize = parseFloat(styles.fontSize) || 13;
      let lineHeight = parseFloat(styles.lineHeight);
      if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
        lineHeight = fontSize * 1.35;
      }
      const maxHeight = lineHeight * lines;

      const previousMaxHeight = el.style.maxHeight;
      const previousOverflow = el.style.overflow;
      el.style.maxHeight = 'none';
      el.style.overflow = 'visible';
      const fullHeight = el.scrollHeight;
      el.style.maxHeight = previousMaxHeight;
      el.style.overflow = previousOverflow;

      setNeedsClamp(fullHeight > maxHeight + 1);
    };

    measure();

    let frame = 0;
    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(scheduleMeasure)
      : null;
    observer?.observe(el);

    window.addEventListener('resize', scheduleMeasure);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [content, lines, expanded]);

  if (!content) {
    return <p className="details-text">—</p>;
  }

  return (
    <div className="details-text-block">
      <p
        ref={textRef}
        className={`details-text${expanded ? ' is-expanded' : ' is-clamped'}`}
        style={
          expanded
            ? undefined
            : { maxHeight: `calc(1.35em * ${lines})` }
        }
      >
        {content}
      </p>
      {needsClamp && (
        <button
          type="button"
          className="details-text-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

export default ClampedDetailsText;
